/**
 * <gw-scroll-row> — the brand pill row on the collection and search grids.
 *
 * The row is a plain `overflow-x: auto` strip with its scrollbar hidden
 * (`gw-collection-grid-styles`). Once it listed every vendor it outgrew desktop
 * screens and nothing said so: at 1440px the last visible pill ended flush with
 * the edge, so the row read as complete. This element adds what the client
 * asked for:
 *
 * 1. EDGE STATE — `data-more-left` / `data-more-right` flag an edge with pills
 *    beyond it; the stylesheet fades that edge (desktop only).
 * 2. MOUSE WHEEL — a vertical wheel over the row scrolls it sideways.
 * 3. MOUSE DRAG — press and drag to scroll. A press that moves under 5px is
 *    still a click; past that it is a drag, and the click its release would
 *    fire on a pill is swallowed, so a drag never follows a link.
 * 4. SELECTED BRAND IN VIEW — a pill click reloads the page with the row back
 *    at the start, which can leave the selected brand off-screen. On load the
 *    row centres the active pill, unless it is already clear of the fade.
 *
 * `gw-reviews-carousel` deliberately refused (2): mapped naively, the page stops
 * dead whenever the row slides under a resting cursor. Two rules keep the page
 * in charge here:
 *
 * - The wheel drives the row only once the mouse has MOVED over it. Content
 *   scrolling under a still cursor fires no real pointermove, so a page scroll
 *   that carries the row past the cursor keeps scrolling the page.
 * - At either end the wheel falls through to the page, and any page scroll
 *   disarms the row again, so the cursor cannot get stuck on it.
 *
 * Trackpad sideways swipes, shift+wheel, touch and keyboard stay native.
 */
class GwScrollRow extends HTMLElement {
  constructor() {
    super();

    this.armed = false;
    this.lastWheel = -Infinity;
    this.press = null;
    this.suppressClick = false;
    this.update = this.throttle(() => this.updateEdges());
    this.disarm = () => (this.armed = false);

    this.addEventListener('scroll', this.update, { passive: true });
    this.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
    this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (event.movementX || event.movementY) this.armed = true;
      this.onDrag(event);
    });
    this.addEventListener('pointerup', (event) => this.endDrag(event));
    this.addEventListener('pointercancel', (event) => this.endDrag(event));
    this.addEventListener('lostpointercapture', (event) => this.endDrag(event));
    this.addEventListener('pointerleave', this.disarm);
    // The pills are links, and dragging a link starts the browser's own
    // drag-and-drop, which cancels the pointer stream mid-drag.
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  connectedCallback() {
    window.addEventListener('scroll', this.disarm, { passive: true });

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this);
    }

    this.revealActive();
    // A late webfont swap changes pill widths without resizing the row. Re-run
    // the reveal only if the visitor has not moved the row in the meantime.
    document.fonts?.ready.then(() => {
      if (Math.abs(this.scrollLeft - this.revealedAt) < 1) this.revealActive();
      this.update();
    });
    this.updateEdges();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.disarm);
    this.observer?.disconnect();
  }

  /** rAF-coalesced callback. */
  throttle(fn) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fn();
      });
    };
  }

  get behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Sets scrollLeft directly: `scrollIntoView` would also scroll the PAGE to the
   * row whenever the row is off-screen (e.g. a restored scroll position).
   */
  revealActive() {
    const pill = this.querySelector('.gw-cgrid__pill--active');
    if (pill) {
      const row = this.getBoundingClientRect();
      const box = pill.getBoundingClientRect();
      // 80px = the desktop edge fade in gw-collection-grid-styles; a pill under
      // it reads as "more this way", not as the selected brand.
      if (box.left < row.left || box.right > row.right - 80) {
        this.scrollLeft += box.left - row.left - (row.width - box.width) / 2;
      }
    }
    this.revealedAt = this.scrollLeft;
  }

  updateEdges() {
    const max = this.scrollWidth - this.clientWidth;
    this.toggleAttribute('data-more-left', this.scrollLeft > 1);
    this.toggleAttribute('data-more-right', this.scrollLeft < max - 1);
  }

  onWheel(event) {
    if (!this.armed || event.ctrlKey || event.shiftKey) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    const max = this.scrollWidth - this.clientWidth;
    const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? this.clientWidth : 1;
    // Rapid ticks build on the pending target; restarting from the mid-animation
    // position would drop distance on every tick.
    const from = event.timeStamp - this.lastWheel < 300 ? this.target : this.scrollLeft;
    const to = Math.min(max, Math.max(0, from + event.deltaY * unit));
    // Nowhere left to go: hand the wheel to the page. The 1px slack absorbs the
    // fractional scrollLeft a zoomed or high-DPI screen stops at, which would
    // otherwise swallow the wheel at the very end.
    if (Math.abs(to - from) < 1) return;

    event.preventDefault();
    this.target = to;
    this.lastWheel = event.timeStamp;
    this.scrollTo({ left: to, behavior: this.behavior });
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.scrollWidth - this.clientWidth < 1) return;
    this.press = { id: event.pointerId, x: event.clientX, left: this.scrollLeft, dragging: false };
  }

  onDrag(event) {
    const press = this.press;
    if (!press || event.pointerId !== press.id) return;
    // A release outside the row before the drag began never reached us.
    if (!(event.buttons & 1)) return this.endDrag(event);

    const dx = event.clientX - press.x;
    if (!press.dragging) {
      if (Math.abs(dx) < 5) return;
      press.dragging = true;
      // Keeps the drag alive when the cursor leaves the row mid-drag.
      this.setPointerCapture(press.id);
      this.toggleAttribute('data-dragging', true);
      this.lastWheel = -Infinity;
    }
    this.scrollLeft = press.left - dx;
  }

  endDrag(event) {
    const press = this.press;
    if (!press || event.pointerId !== press.id) return;

    this.press = null;
    if (!press.dragging) return;

    this.removeAttribute('data-dragging');
    // The click this release fires lands on whichever pill is under the
    // cursor. Swallow that one only: the timeout clears the flag even when no
    // click follows, so it can never eat a later, genuine click.
    this.suppressClick = true;
    setTimeout(() => (this.suppressClick = false));
  }
}

if (!customElements.get('gw-scroll-row')) {
  customElements.define('gw-scroll-row', GwScrollRow);
}
